// Extension of the existing professor editor: native checkboxes and selects,
// explicit independent save, inherited role access kept visible and unchanged.
const StaffModuleAccess = {
    state: null,
    async mount(professorId) {
        const root = document.getElementById('staff-module-access');
        if (!root || !AuthManager.isAdmin()) return;
        const state = {root, professorId, epoch:AuthManager._epoch, dirty:false, saving:false, expanded:false};
        this.state = state;
        try {
            const result = await this.request(state, 'GET');
            if (!this.current(state)) return;
            Object.assign(state, result);
            state.userId = state.accounts.length === 1 ? state.accounts[0].id : '';
            this.render(state);
        } catch (error) {
            if (!this.current(state)) return;
            root.replaceChildren();
            const message = document.createElement('p');
            message.setAttribute('role','alert'); message.textContent = error.message;
            const retry = document.createElement('button');
            retry.type = 'button'; retry.className = 'btn btn-secondary'; retry.textContent = 'Reintentar carga';
            retry.addEventListener('click',()=>this.mount(professorId));
            root.append(message,retry);
        }
    },
    current(state) { return this.state === state && state.root.isConnected && state.epoch === AuthManager._epoch && AuthManager.isAdmin(); },
    hasPending() { return this.state && this.current(this.state) && (this.state.dirty || this.state.saving); },
    async request(state, method, body) {
        const response = await fetch('/api/professors/'+encodeURIComponent(state.professorId)+'/module-access', {
            method, cache:'no-store', headers:{'Content-Type':'application/json','X-SICAF-CSRF':AuthManager.getCsrfToken()},
            ...(body ? {body:JSON.stringify(body)} : {})
        });
        const result = await response.json().catch(()=>null);
        if (!response.ok || !result?.success) throw new Error(result?.error || 'No se pudieron cargar los accesos. Reintenta la operación.');
        return result;
    },
    render(state) {
        const esc = Utils.sanitizeHTML;
        const account = state.accounts.find(a=>a.id===state.userId);
        SafeHTML.set(state.root, `
            <details class="staff-access-disclosure" ${state.expanded?'open':''}>
            <summary><span>Accesos por módulo</span><span id="staff-access-count">${Object.keys(account?.permissions || {}).length} adicionales</span></summary>
            <div class="staff-access-content">
            <p>Selecciona varios módulos y el nivel de acceso. No cambia su rol ni retira los permisos que ya tiene.</p>
            ${!state.accounts.length ? '<p role="status">Esta ficha aún no tiene cuenta de acceso. Crea la cuenta y vuelve a abrir el editor.</p>' : `
            <label class="form-label" for="staff-access-account">Cuenta del trabajador</label>
            <select class="form-select" id="staff-access-account">
                <option value="">Selecciona una cuenta…</option>
                ${state.accounts.map(a=>`<option value="${esc(a.id)}" ${a.id===state.userId?'selected':''}>@${esc(a.username)} · ${esc(a.role)}</option>`).join('')}
            </select>
            ${account && !account.eligible ? '<p role="status">Estos permisos son solo para cuentas de trabajadores. Las cuentas de administración ya tienen acceso completo.</p>' : ''}
            ${account?.eligible ? `
                ${!account.active ? '<p role="status">Acceso del trabajador desactivado. Solo puedes retirar permisos; para añadirlos activa primero la ficha y vuelve a abrirla.</p>' : ''}
                <label class="form-label" for="staff-access-search">Buscar módulo</label>
                <input id="staff-access-search" class="form-input" type="search" placeholder="Calendario, alumnado, nóminas…" autocomplete="off">
                <div class="staff-access-list" tabindex="0" aria-label="Módulos disponibles">
                    ${state.catalog.map((item,index)=>{
                        const inherited = item.managers.includes(account.role);
                        const level = account.permissions[item.id] || '';
                        return `${index===0 || item.group!==state.catalog[index-1].group ? `<h5 class="staff-access-group">${esc(item.group || 'Módulos')}</h5>`:''}<div class="staff-access-row" data-access-name="${esc(item.name)}">
                            <div><label class="staff-access-label"><input type="checkbox" data-access-module="${item.id}" ${level||inherited?'checked':''} ${inherited?'disabled':''}><span>${esc(item.name)}</span></label>
                            <p id="access-help-${item.id}">${esc(item.description)}${inherited?' <strong>Gestionar incluido en su rol.</strong>':''}</p></div>
                            <select class="form-select" data-access-level="${item.id}" aria-label="Nivel de acceso: ${esc(item.name)}" aria-describedby="access-help-${item.id}" ${!level||inherited?'disabled':''}>
                                <option value="read" ${level!=='manage'&&!inherited?'selected':''}>Consultar</option>
                                ${item.levels?.includes('manage')!==false ? `<option value="manage" ${level==='manage'||inherited?'selected':''}>Gestionar</option>` : ''}
                            </select>
                        </div>`;
                    }).join('')}
                </div>
                <p id="staff-access-empty" hidden>No hay módulos que coincidan con tu búsqueda.</p>
                <p>Consultar no permite gestionar. Los permisos incluidos en su rol se conservan. Administrar cuentas y conceder permisos sigue reservado al administrador.</p>
                <div class="staff-access-actions"><button type="button" class="btn btn-secondary" id="staff-access-save" disabled>Guardar accesos</button><span role="status" id="staff-access-status">Sin cambios pendientes</span></div>
            ` : ''}`}</div></details>`);
        state.root.querySelector('details').addEventListener('toggle',event=>{state.expanded=event.target.open;});
        state.root.querySelector('#staff-access-search')?.addEventListener('input',event=>{
            const normalize=text=>text.normalize('NFD').replace(/[\u0300-\u036f]/g,'').toLowerCase();
            const query=normalize(event.target.value.trim());
            let matches=0;
            state.root.querySelectorAll('.staff-access-row').forEach(row=>{
                row.hidden=!normalize(row.dataset.accessName).includes(query);
                if(!row.hidden)matches++;
            });
            state.root.querySelectorAll('.staff-access-group').forEach(heading=>{
                let row=heading.nextElementSibling, visible=false;
                while(row && !row.classList.contains('staff-access-group')){visible ||= !row.hidden;row=row.nextElementSibling;}
                heading.hidden=!visible;
            });
            state.root.querySelector('#staff-access-empty').hidden=matches!==0;
        });
        state.root.querySelector('#staff-access-account')?.addEventListener('change',event=>{
            if (state.dirty || state.saving) {
                event.target.value=state.userId;
                Utils.showToast('Guarda los accesos pendientes antes de cambiar de cuenta.','warning');
                return;
            }
            state.userId=event.target.value; this.render(state);
        });
        state.root.querySelectorAll('[data-access-module], [data-access-level]').forEach(control=>control.addEventListener('change',()=>{
            const id=control.dataset.accessModule;
            if (id) state.root.querySelector(`[data-access-level="${id}"]`).disabled = !control.checked;
            state.dirty=true;
            state.root.querySelector('#staff-access-save').disabled=false;
            state.root.querySelector('#staff-access-status').textContent='Cambios sin guardar';
            const count=state.root.querySelectorAll('[data-access-module]:checked:not(:disabled)').length;
            state.root.querySelector('#staff-access-count').textContent=count+' adicionales · sin guardar';
        }));
        state.root.querySelector('#staff-access-save')?.addEventListener('click',()=>this.save(state));
    },
    async save(state) {
        if (!this.current(state) || !state.dirty || state.saving) return;
        const account=state.accounts.find(a=>a.id===state.userId), permissions={};
        for (const check of state.root.querySelectorAll('[data-access-module]')) {
            if (check.disabled) { // Preserve explicit grants; the base role is never rewritten.
                if (account.permissions[check.dataset.accessModule]) permissions[check.dataset.accessModule]=account.permissions[check.dataset.accessModule];
            } else if (check.checked) permissions[check.dataset.accessModule]=state.root.querySelector(`[data-access-level="${check.dataset.accessModule}"]`).value;
        }
        state.saving=true;
        const disabledBefore = [...state.root.querySelectorAll('input,select,button')].map(el=>[el,el.disabled]);
        state.root.querySelectorAll('input,select,button').forEach(el=>el.disabled=true);
        state.root.querySelector('#staff-access-status').textContent='Guardando accesos…';
        try {
            const result=await this.request(state,'PUT',{userId:account.id,permissions,expectedVersion:account.version});
            if (!this.current(state)) return;
            Object.assign(state,result,{dirty:false,saving:false});
            this.render(state);
            state.root.querySelector('#staff-access-status').textContent='Accesos guardados. Aplicados en el servidor; el menú se actualiza en unos segundos.';
        } catch(error) {
            if (!this.current(state)) return;
            // Retain the draft instead of silently replacing it on a failed save.
            state.saving=false;
            disabledBefore.forEach(([el,disabled])=>el.disabled=disabled);
            state.root.querySelector('#staff-access-save').disabled=false;
            const status=state.root.querySelector('#staff-access-status');
            status.setAttribute('role','alert'); status.textContent=error.message;
        }
    }
};
window.StaffModuleAccess=StaffModuleAccess;
